<template>
  <div class="history-tasks-page">
    <!-- 顶部导航栏 -->
    <div class="top-bar">
      <button class="ghost-btn" @click="goBack">
        <span class="icon">←</span> 返回
      </button>
      <div class="title">📋 历史任务</div>
      <div class="actions">
        <button class="primary-btn" @click="loadTasks">🔄 刷新</button>
      </div>
    </div>

    <!-- 任务列表 -->
    <div class="tasks-container">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>加载中...</p>
      </div>
      <div v-else-if="tasks.length === 0" class="empty-state">
        <div class="empty-icon">📋</div>
        <p>暂无历史任务</p>
        <p class="empty-hint">执行任务后，结果将保存在此处</p>
      </div>
      <div v-else class="task-table">
        <div class="task-table-header">
          <span class="col-tool">工具名称</span>
          <span class="col-status">状态</span>
          <span class="col-time">创建时间</span>
          <span class="col-action">操作</span>
        </div>
        <div v-for="task in tasks" :key="task.id" class="task-row">
          <div class="task-row-header" @click="toggleExpand(task.id)">
            <span class="col-tool">
              <span class="expand-icon">{{ isExpanded(task.id) ? '▼' : '▶' }}</span>
              {{ task.tool_title || '未知工具' }}
            </span>
            <span class="col-status">
              <span class="task-status-tag" :class="task.status">{{ getStatusText(task.status) }}</span>
            </span>
            <span class="col-time">{{ formatDate(task.created_at) }}</span>
            <span class="col-action">
              <button class="icon-btn delete" @click.stop="deleteTaskItem(task.id)" title="删除">🗑</button>
            </span>
          </div>
          <div v-if="isExpanded(task.id)" class="task-row-body">
            <div class="detail-line">
              <span class="detail-label">任务ID：</span>
              <code class="detail-value">{{ task.id }}</code>
            </div>
            <div v-if="task.outputDir" class="detail-line">
              <span class="detail-label">输出目录：</span>
              <code class="detail-value">{{ task.outputDir }}</code>
            </div>
            <div v-if="task.status === 'SUCCESS'" class="detail-actions">
              <button class="primary-btn small" @click="downloadResult(task)">⬇ 下载全部(.zip)</button>
            </div>
            <div v-if="task.result_files && task.result_files.length > 0" class="result-files-compact">
              <div class="result-files-title">📁 结果文件</div>
              <div v-for="(file, idx) in task.result_files" :key="idx" class="result-file-item">
                <span class="result-file-name">{{ file.name }}</span>
                <span class="result-file-size">{{ formatFileSize(file.size) }}</span>
                <button class="download-file-btn" @click="downloadResultFile(task.id, file.path)">下载</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 下载进度条 -->
    <div v-if="isDownloading" class="download-overlay">
      <div class="download-panel">
        <div class="download-title">⬇️ 正在下载</div>
        <div class="download-filename">{{ downloadFileName }}</div>
        <div class="progress-track">
          <div class="progress-fill-bar" :style="{ width: downloadProgress + '%' }"></div>
        </div>
        <div class="progress-text">{{ downloadProgress }}%</div>
      </div>
    </div>

    <!-- Toast -->
    <div v-if="showToast" :class="['toast', toastType]">
      {{ toastMessage }}
    </div>
  </div>
</template>

<script>
import { getUserTasks, deleteTask } from '../api'

export default {
  name: 'HistoryTasks',
  data() {
    return {
      tasks: [],
      loading: false,
      showToast: false,
      toastMessage: '',
      toastType: 'success',
      isDownloading: false,
      downloadProgress: 0,
      downloadFileName: '',
      expandedTasks: {}
    }
  },
  created() {
    this.loadTasks()
  },
  methods: {
    goBack() {
      this.$emit('back')
    },
    async loadTasks() {
      this.loading = true
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
        const userId = userInfo.user_id || 0
        const res = await getUserTasks(userId)
        if (res.code === 200) {
          this.tasks = res.data || []
        } else {
          this.showToastMessage(res.message || '加载失败', 'error')
        }
      } catch (error) {
        console.error('Load tasks error:', error)
        this.showToastMessage('加载历史任务失败', 'error')
      } finally {
        this.loading = false
      }
    },
    async deleteTaskItem(taskId) {
      if (!confirm('确定要删除该任务记录吗？')) return
      try {
        const res = await deleteTask(taskId)
        if (res.code === 200) {
          this.tasks = this.tasks.filter(t => t.id !== taskId)
          this.showToastMessage('任务已删除', 'success')
        } else {
          this.showToastMessage(res.message || '删除失败', 'error')
        }
      } catch (error) {
        this.showToastMessage('删除失败', 'error')
      }
    },
    downloadWithProgress(url, filename) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        this.isDownloading = true
        this.downloadProgress = 0
        this.downloadFileName = filename

        xhr.open('GET', url, true)
        xhr.responseType = 'blob'

        xhr.onprogress = (event) => {
          if (event.lengthComputable) {
            this.downloadProgress = Math.round((event.loaded / event.total) * 100)
          }
        }

        xhr.onload = () => {
          this.isDownloading = false
          if (xhr.status === 200) {
            const blob = new Blob([xhr.response])
            const downloadUrl = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = downloadUrl
            a.download = filename
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(downloadUrl)
            resolve()
          } else {
            reject(new Error(`下载失败: ${xhr.status}`))
          }
        }

        xhr.onerror = () => {
          this.isDownloading = false
          reject(new Error('下载失败'))
        }

        xhr.send()
      })
    },
    async downloadResult(task) {
      if (task.status !== 'SUCCESS') {
        this.showToastMessage('任务尚未完成', 'warning')
        return
      }
      const url = `/api/tasks/${task.id}/result/download`
      try {
        await this.downloadWithProgress(url, `result_${task.id}.zip`)
        this.showToastMessage('下载成功', 'success')
      } catch (error) {
        this.showToastMessage('下载失败', 'error')
      }
    },
    async downloadResultFile(taskId, filePath) {
      const encodedPath = encodeURIComponent(filePath)
      const url = `/api/tasks/${taskId}/result/download?path=${encodedPath}`
      const filename = filePath.split('/').pop()
      try {
        await this.downloadWithProgress(url, filename)
        this.showToastMessage('下载成功', 'success')
      } catch (error) {
        this.showToastMessage('下载失败', 'error')
      }
    },
    getStatusText(status) {
      const map = {
        'PENDING': '等待中',
        'RUNNING': '执行中',
        'SUCCESS': '成功',
        'FAILED': '失败'
      }
      return map[status] || status
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleString('zh-CN')
    },
    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    toggleExpand(taskId) {
      this.$set(this.expandedTasks, taskId, !this.expandedTasks[taskId])
    },
    isExpanded(taskId) {
      return !!this.expandedTasks[taskId]
    },
    showToastMessage(message, type = 'success') {
      this.toastMessage = message
      this.toastType = type
      this.showToast = true
      setTimeout(() => {
        this.showToast = false
      }, 3000)
    }
  }
}
</script>

<style scoped>
.history-tasks-page {
  min-height: 100vh;
  background: #f0f4f8;
  font-family: 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

.top-bar {
  height: 56px;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
  position: sticky;
  top: 0;
}

.title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  flex: 1;
  text-align: center;
}

.ghost-btn {
  background: #fff;
  border: 1px solid #cbd5e1;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  color: #475569;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.primary-btn {
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  border: none;
  padding: 8px 18px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}

.actions {
  display: flex;
  gap: 10px;
}

.tasks-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 24px;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: #94a3b8;
}

.empty-icon {
  font-size: 56px;
  margin-bottom: 16px;
}

.empty-hint {
  font-size: 14px;
  margin-top: 8px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #0ea5e9;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.task-table {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.task-table-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1.5fr 80px;
  padding: 12px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
}

.task-row {
  border-bottom: 1px solid #f1f5f9;
}

.task-row:last-child {
  border-bottom: none;
}

.task-row-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1.5fr 80px;
  align-items: center;
  padding: 14px 20px;
  cursor: pointer;
  transition: background 0.15s;
  font-size: 14px;
}

.task-row-header:hover {
  background: #f8fafc;
}

.expand-icon {
  display: inline-block;
  width: 18px;
  color: #94a3b8;
  font-size: 10px;
  margin-right: 4px;
}

.col-tool {
  color: #1e293b;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-status {
  text-align: left;
}

.col-time {
  color: #64748b;
  font-size: 13px;
}

.col-action {
  text-align: center;
}

.icon-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 14px;
  transition: background 0.2s;
}

.icon-btn:hover {
  background: #f1f5f9;
}

.task-status-tag {
  display: inline-block;
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 20px;
  font-weight: 500;
}

.task-status-tag.PENDING { background: #fef3c7; color: #92400e; }
.task-status-tag.RUNNING { background: #dbeafe; color: #1e40af; }
.task-status-tag.SUCCESS { background: #d1fae5; color: #065f46; }
.task-status-tag.FAILED { background: #fee2e2; color: #991b1b; }

.task-row-body {
  padding: 16px 20px 16px 42px;
  background: #fafbfc;
  border-top: 1px dashed #e2e8f0;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}

.detail-line {
  display: flex;
  align-items: baseline;
  margin-bottom: 8px;
  font-size: 13px;
}

.detail-label {
  color: #64748b;
  font-weight: 500;
  min-width: 70px;
  flex-shrink: 0;
}

.detail-value {
  color: #334155;
  word-break: break-all;
  font-family: 'Fira Code', monospace;
  font-size: 12px;
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 4px;
}

.detail-actions {
  margin: 10px 0;
}

.primary-btn.small {
  padding: 6px 14px;
  font-size: 13px;
}

.result-files-compact {
  margin-top: 10px;
}

.result-files-compact .result-files-title {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 8px;
}

.result-files-compact .result-file-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: #f8fafc;
  border-radius: 6px;
  margin-bottom: 6px;
  font-size: 13px;
}

.result-file-name {
  flex: 1;
  color: #334155;
  word-break: break-all;
}

.result-file-size {
  color: #94a3b8;
  font-size: 12px;
  white-space: nowrap;
}

.download-file-btn {
  background: #e0f2fe;
  color: #0369a1;
  border: none;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s;
  white-space: nowrap;
}

.download-file-btn:hover {
  background: #bae6fd;
}

/* 下载进度条 */
.download-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9998;
}

.download-panel {
  background: #fff;
  border-radius: 16px;
  padding: 32px 40px;
  width: 420px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  text-align: center;
}

.download-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.download-filename {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 20px;
  word-break: break-all;
}

.progress-track {
  width: 100%;
  height: 10px;
  background: #e2e8f0;
  border-radius: 5px;
  overflow: hidden;
  margin-bottom: 10px;
}

.progress-fill-bar {
  height: 100%;
  background: linear-gradient(90deg, #0ea5e9, #0284c7);
  border-radius: 5px;
  transition: width 0.2s ease;
}

.progress-text {
  font-size: 14px;
  font-weight: 600;
  color: #0ea5e9;
}

/* Toast */
.toast {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 24px;
  border-radius: 10px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  z-index: 9999;
  animation: slideDown 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.toast.success { background: linear-gradient(135deg, #10b981, #059669); }
.toast.error { background: linear-gradient(135deg, #ef4444, #dc2626); }
.toast.warning { background: linear-gradient(135deg, #f59e0b, #d97706); }

@keyframes slideDown {
  from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}
</style>
